<template>
  <div class="service-menu">
    <div
      v-if="loading && menuData.length === 0"
      class="service-menu__skeleton"
      role="status"
      aria-label="分类加载中"
    >
      <a-skeleton :animation="true">
        <div
          v-for="(width, index) in menuSkeletonWidths"
          :key="index"
          class="service-menu__skeleton-row"
          :class="{ 'service-menu__skeleton-row--child': index === 2 || index === 5 }"
        >
          <a-skeleton-line :rows="1" :widths="[width]" :line-height="16" />
        </div>
      </a-skeleton>
    </div>
    <a-menu
      v-else
      v-model:open-keys="openKeys"
      mode="vertical"
      :scroll-config="{ scrollbar: { style: { width: '3px' } } }"
      :selected-keys="computedSelectedKeys"
      :accordion="accordion"
      class="service-menu__tree"
      :level-indent="levelIndent"
      @menu-item-click="handleMenuClick"
      @sub-menu-click="handleSubMenuClick"
    >
      <MenuTree :nodes="visibleMenuData" :selected-path-keys="selectedPathKeys" />
    </a-menu>
  </div>
</template>

<script setup>
/**
 * @description 帮助中心左侧树形菜单组件
 * @param {Array<string>} categories - 需要请求的分类类型：'product' | 'scene' | 'help'，默认为 ['product', 'scene', 'help']
 * @param {string|number|Array<string|number>} selectedKey - 当前选中的菜单项ID（单选时为单个值，多选时为数组）
 * @param {boolean} multiple - 是否多选模式，默认为 false（单选）
 * @param {boolean} accordion - 是否手风琴模式（同时只能展开一个父菜单），手风琴模式下强制单选
 * @param {string} defaultExpand - 默认展开情况：'all' | 'first' | 'none'，默认为 'all'
 * @param {string} contentCenterType - 内容分类所属中心类型
 * @param {string} contentCategoryRootName - 内容分类根节点名称
 * @param {string} contentCategoryLabel - 内容分类菜单标题
 * @emits select - 选中菜单项时触发，单选时传递单个分类ID，多选时传递分类ID数组
 */

import {
  ref,
  watch,
  onMounted,
  computed,
  defineComponent,
  h,
  resolveComponent,
  nextTick,
} from 'vue'
import { BaseMessage } from '~/utils/baseMessage'
import { websiteApi } from '@/api/website'
import { baseApi } from '@/api/base'
import { createPublicContentSsrRequestConfig } from '~/utils/website/ssrRequestPolicy'

const levelIndent = computed(() => 12)

const props = defineProps({
  categories: {
    type: Array,
    default: () => ['product', 'scene', 'help'],
    validator: (value) => {
      const validTypes = ['product', 'scene', 'help']
      return Array.isArray(value) && value.every((type) => validTypes.includes(type))
    },
  },
  selectedKey: {
    type: [String, Number, Array],
    default: null,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  accordion: {
    type: Boolean,
    default: false,
  },
  defaultExpand: {
    type: String,
    default: 'all',
    validator: (value) => ['all', 'first', 'none'].includes(value),
  },
  contentCenterType: {
    type: String,
    default: 'HELP',
  },
  contentCategoryRootName: {
    type: String,
    default: '帮助中心',
  },
  contentCategoryLabel: {
    type: String,
    default: '问题类型',
  },
  facetCounts: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['select'])

const menuData = ref([])
// 首次渲染早于 onMounted 请求；初始即标记为加载中，避免空菜单闪屏。
const loading = ref(true)
const visibleMenuData = computed(() => {
  if (!props.facetCounts) return menuData.value
  const dimensions = {
    'product-type': 'product',
    'application-scene': 'scene',
    'question-type': 'category',
  }
  const filterNode = (node, dimension) => {
    const children = Array.isArray(node.children) ? node.children : []
    if (children.length) {
      const visible = children.map((child) => filterNode(child, dimension)).filter(Boolean)
      return visible.length ? { ...node, children: visible } : null
    }
    const count = Number(props.facetCounts[dimension]?.[String(node.contentCategoryId)]) || 0
    const wildcard =
      dimension === 'product' ? Number(props.facetCounts.productWildcardCount) || 0 : 0
    return count + wildcard > 0 ? node : null
  }
  return menuData.value.flatMap((node) => {
    if (node.key === 'all') return [node]
    const dimension = dimensions[node.contentCategoryId]
    if (!dimension) return [node]
    const filtered = filterNode(node, dimension)
    return filtered ? [filtered] : []
  })
})

watch([visibleMenuData, loading], ([nodes, isLoading]) => {
  if (!props.facetCounts || isLoading || !menuData.value.length || props.selectedKey === 'all')
    return
  const containsKey = (items, key) =>
    items.some((item) => item.key === key || containsKey(item.children || [], key))
  if (!containsKey(nodes, String(props.selectedKey))) emit('select', 'all')
})
const menuSkeletonWidths = ['34%', '56%', '42%', '60%', '48%', '38%', '54%']
const openKeys = ref([])
const hasManualOpen = ref(false)
const lastOpenKey = ref(null)

const MenuTree = defineComponent({
  name: 'MenuTree',
  props: {
    nodes: {
      type: Array,
      default: () => [],
    },
    selectedPathKeys: {
      type: Array,
      default: () => [],
    },
  },
  setup(props) {
    const renderNode = (node) => {
      const inSelectedPath =
        Array.isArray(props.selectedPathKeys) && props.selectedPathKeys.includes(node.key)
      const hasChildren = Array.isArray(node.children) && node.children.length > 0
      if (hasChildren) {
        return h(
          resolveComponent('a-sub-menu'),
          {
            key: node.key,
            'data-menu-key': node.key,
            class: inSelectedPath ? 'service-menu__in-selected-path' : undefined,
          },
          {
            title: () => h('span', node.categoryName),
            default: () => node.children.map((child) => renderNode(child)),
          }
        )
      }
      return h(
        resolveComponent('a-menu-item'),
        {
          key: node.key,
          'data-menu-key': node.key,
          class: [
            node.key === 'all' ? 'service-menu__item-all' : undefined,
            inSelectedPath ? 'service-menu__in-selected-path' : undefined,
          ].filter(Boolean),
        },
        () => node.categoryName
      )
    }
    return () => props.nodes.map((node) => renderNode(node))
  },
})

/**
 * @description 判断节点是否需要作为菜单项渲染（有有效 id 与名称）
 * @param {Object} node - 节点
 * @returns {boolean}
 */
const shouldRenderMenuNode = (node) => {
  if (!node || node.contentCategoryId == null || String(node.contentCategoryId).trim() === '')
    return false
  if (node.categoryName == null || String(node.categoryName).trim() === '') return false
  return true
}

/**
 * @description 扁平化不渲染的层级：不渲染的节点直接删除，其子节点挂到父节点下
 * @param {Array} items - 当前层级节点列表
 * @returns {Array} - 扁平化后的节点列表（仅包含需渲染的节点，其 children 已递归处理）
 */
const flattenNonRenderedLevels = (items) => {
  if (!Array.isArray(items)) return []
  const result = []
  for (const item of items) {
    if (!shouldRenderMenuNode(item)) {
      const children = Array.isArray(item?.children) ? item.children : []
      result.push(...flattenNonRenderedLevels(children))
    } else {
      const nextChildren = flattenNonRenderedLevels(item.children || [])
      const node = {
        ...item,
        children: nextChildren.length > 0 ? nextChildren : null,
      }
      result.push(node)
    }
  }
  return result
}

/**
 * @description 为菜单项添加key字段（递归处理子项）
 * @param {Object} item - 菜单项对象
 * @param {Array<string>} parentPath - 父级路径数组
 * @returns {Object} - 添加了key字段的菜单项
 */
const addMenuKey = (item, parentPath = []) => {
  if (!item || !item.contentCategoryId) {
    return item
  }

  const currentPath = [...parentPath, String(item.contentCategoryId)]
  const key = currentPath.join('-')

  const result = {
    ...item,
    key,
  }

  if (item.children && Array.isArray(item.children) && item.children.length > 0) {
    result.children = item.children.map((child) => addMenuKey(child, currentPath))
  }

  return result
}

/**
 * @description 计算是否为多选模式（手风琴模式下强制单选）
 * @returns {boolean}
 */
const isMultipleMode = computed(() => {
  return props.multiple && !props.accordion
})

/**
 * @description 计算选中的菜单项keys数组（原模原样返回，不做转换）
 * @returns {Array<string>}
 */
const computedSelectedKeys = computed(() => {
  if (!props.selectedKey) {
    return []
  }

  if (isMultipleMode.value) {
    // 多选模式：selectedKey 应该是数组
    const keys = Array.isArray(props.selectedKey) ? props.selectedKey : [props.selectedKey]
    return keys.map((k) => String(k))
  } else {
    // 单选模式：selectedKey 是单个值
    return [String(props.selectedKey)]
  }
})

/**
 * @description 选中项及其所有祖先的 key 列表，用于「从顶级到选中层级」加粗
 */
const selectedPathKeys = computed(() => {
  if (props.selectedKey == null || props.selectedKey === '') return []
  return getAncestorKeys(String(props.selectedKey))
})

/**
 * @description 处理菜单项点击
 * @param {string} key - 被点击的菜单项key（带路径）
 */
const handleMenuClick = (key) => {
  if (isMultipleMode.value) {
    // 多选模式：切换选中状态
    const currentKeys = Array.isArray(props.selectedKey)
      ? [...props.selectedKey]
      : props.selectedKey
        ? [props.selectedKey]
        : []
    const keyStr = String(key)
    const keyIndex = currentKeys.findIndex((k) => String(k) === keyStr)

    if (keyIndex >= 0) {
      // 已选中，取消选中
      currentKeys.splice(keyIndex, 1)
    } else {
      // 未选中，添加到选中列表
      currentKeys.push(key)
    }

    emit('select', currentKeys.length > 0 ? currentKeys : null)
  } else {
    // 单选模式：直接选中
    emit('select', key)
    // 选中后保持该项在可视区内，避免被顶出视野需要用户滚上去找
    nextTick(() => {
      if (!import.meta.client) return
      setTimeout(() => {
        const target = document.querySelector(`[data-menu-key="${key}"]`)
        const container =
          target?.closest('.help-sidebar__menu-inner') || target?.closest('.service-menu')
        if (!target || !container) return
        const containerRect = container.getBoundingClientRect()
        const targetRect = target.getBoundingClientRect()
        const topDiff = targetRect.top - containerRect.top
        const bottomDiff = targetRect.bottom - containerRect.bottom
        if (topDiff < 0) {
          container.scrollTop += topDiff
        } else if (bottomDiff > 0) {
          container.scrollTop += bottomDiff
        }
      }, 80)
    })
  }
}

/**
 * @description 根据当前key解析出所有祖先节点key（包括当前节点）
 * @param {string} pathKey - 路径key，格式：parent1-parent2-itemId
 * @returns {Array<string>} - 所有祖先节点key数组，从最顶层到当前节点
 * @example
 * getAncestorKeys('product-type-category-123-item-456')
 * // 返回: ['product-type', 'product-type-category-123', 'product-type-category-123-item-456']
 */
const getAncestorKeys = (pathKey) => {
  const parts = String(pathKey).split('-')
  if (parts.length === 0) {
    return []
  }

  const ancestorKeys = []
  for (let i = 1; i <= parts.length; i++) {
    ancestorKeys.push(parts.slice(0, i).join('-'))
  }

  return ancestorKeys
}

/**
 * @description 处理手风琴模式下的菜单展开（只关闭同级节点）
 * @param {string} key - 被点击的菜单项key
 * @param {Array<string>} newOpenKeys - 新的展开键数组
 */
const handleSubMenuClick = (key, newOpenKeys) => {
  const isOpening = newOpenKeys.includes(key)

  if (props.accordion && newOpenKeys.length > 0) {
    hasManualOpen.value = true
    // 获取新展开节点的所有祖先节点key（包括当前节点）
    const ancestorKeys = getAncestorKeys(key)
    openKeys.value = newOpenKeys.filter((k) => ancestorKeys.includes(k))
  }

  if (props.accordion && isOpening && import.meta.client) {
    setTimeout(() => {
      const targetKey = lastOpenKey.value || key
      const target = document.querySelector(`[data-menu-key="${targetKey}"]`)
      if (!target) return
      const container =
        target.closest('.help-sidebar__menu-inner') || target.closest('.service-menu')
      if (!container) return

      const containerRect = container.getBoundingClientRect()
      const targetRect = target.getBoundingClientRect()
      const topDiff = targetRect.top - containerRect.top
      const bottomDiff = targetRect.bottom - containerRect.bottom

      if (topDiff < 0) {
        container.scrollTop += topDiff
      } else if (bottomDiff > 0) {
        container.scrollTop += bottomDiff
      }

      lastOpenKey.value = key
    }, 300)
  }
}

/**
 * @description 转换商品分类数据为菜单格式
 */
const transformProductCategories = (categories) => {
  if (!Array.isArray(categories)) return []

  const getDisplayName = (node) => {
    if (node.categoryName) return node.categoryName
    if (node.prodName) {
      return node.modelName ? `${node.modelName} ${node.prodName}` : node.prodName
    }
    return node.modelName || ''
  }

  const collectLeafNodes = (node) => {
    const children = Array.isArray(node.children) ? node.children : []
    if (children.length === 0) {
      return [node]
    }
    return children.flatMap((child) => collectLeafNodes(child))
  }

  const mapLeaf = (node) => ({
    contentCategoryId: node.prodId || node.categoryId,
    categoryName: getDisplayName(node),
    children: null,
  })

  const mapSecondLevel = (node) => {
    const children = Array.isArray(node.children) ? node.children : []
    const leafNodes = children.length > 0 ? collectLeafNodes(node) : []
    return {
      contentCategoryId: node.categoryId,
      categoryName: node.categoryName,
      children: leafNodes.length > 0 ? leafNodes.map(mapLeaf) : null,
    }
  }

  const mapFirstLevel = (node) => ({
    contentCategoryId: node.categoryId,
    categoryName: node.categoryName,
    children: Array.isArray(node.children)
      ? node.children.map((child) => mapSecondLevel(child))
      : null,
  })

  return categories.map((item) => addMenuKey(mapFirstLevel(item)))
}

/**
 * @description 转换场景分类数据为菜单格式
 */
const transformSceneCategories = (scenes) => {
  if (!Array.isArray(scenes)) return []

  const mapSceneNode = (node) => ({
    contentCategoryId: node.id,
    categoryName: node.name,
    children:
      Array.isArray(node.children) && node.children.length > 0
        ? node.children.map((child) => mapSceneNode(child))
        : null,
  })

  return scenes.map((scene) => addMenuKey(mapSceneNode(scene)))
}

/**
 * @description 转换帮助中心分类数据为菜单格式（已符合格式，只需要确保字段正确并添加key）
 */
const transformHelpCategories = (categories) => {
  if (!Array.isArray(categories)) return []
  const mapChildren = (items) => {
    if (!Array.isArray(items)) return []
    return items.map((item) => ({
      ...item,
      children:
        Array.isArray(item.children) && item.children.length > 0
          ? mapChildren(item.children)
          : null,
    }))
  }

  const extractSecondLevel = (items) => {
    return items.flatMap((item) => (Array.isArray(item.children) ? item.children : []))
  }

  const secondLevel = extractSecondLevel(categories)
  return secondLevel.map((category) =>
    addMenuKey({ ...category, children: mapChildren(category.children) })
  )
}

/**
 * @description 获取所有分类树形列表
 */
const fetchCategoryTree = async (requestConfig = {}, { preserveExisting = false } = {}) => {
  loading.value = true
  try {
    const promises = []

    // 根据 categories prop 决定请求哪些接口
    if (props.categories.includes('product')) {
      promises.push(baseApi.getCategoryProdTree(requestConfig).catch(() => []))
    } else {
      promises.push(Promise.resolve(null))
    }

    if (props.categories.includes('scene')) {
      promises.push(
        websiteApi
          .getSceneTree({ centerType: props.contentCenterType }, requestConfig)
          .catch(() => [])
      )
    } else {
      promises.push(Promise.resolve(null))
    }

    if (props.categories.includes('help')) {
      promises.push(
        websiteApi
          .getContentCategoryTree(
            { rootNameContains: props.contentCategoryRootName },
            requestConfig
          )
          .catch(() => [])
      )
    } else {
      promises.push(Promise.resolve(null))
    }

    const [productCategories, sceneCategories, helpCategories] = await Promise.all(promises)

    const menuItems = []

    // 一级项：全部分类（不筛选，显示全部内容）
    const allCategoryItem = {
      key: 'all',
      contentCategoryId: 'all',
      categoryName: '全部分类',
      children: null,
    }

    // 添加商品分类（产品类型）：不渲染的层级删除，子节点挂到父节点
    if (Array.isArray(productCategories) && props.categories.includes('product')) {
      const productMenu = flattenNonRenderedLevels(transformProductCategories(productCategories))
      if (productMenu.length > 0) {
        menuItems.push(
          addMenuKey({
            contentCategoryId: 'product-type',
            categoryName: '产品类型',
            children: productMenu,
          })
        )
      }
    }

    // 添加场景分类（应用场景）：不渲染的层级删除，子节点挂到父节点
    if (
      sceneCategories &&
      Array.isArray(sceneCategories) &&
      sceneCategories.length > 0 &&
      props.categories.includes('scene')
    ) {
      const sceneMenu = flattenNonRenderedLevels(transformSceneCategories(sceneCategories))
      if (sceneMenu.length > 0) {
        menuItems.push(
          addMenuKey({
            contentCategoryId: 'application-scene',
            categoryName: '应用场景',
            children: sceneMenu,
          })
        )
      }
    }

    // 添加帮助中心分类（问题类型）：不渲染的层级删除，子节点挂到父节点
    if (
      helpCategories &&
      Array.isArray(helpCategories) &&
      helpCategories.length > 0 &&
      props.categories.includes('help')
    ) {
      const helpMenu = flattenNonRenderedLevels(transformHelpCategories(helpCategories))
      if (helpMenu.length > 0) {
        menuItems.push(
          addMenuKey({
            contentCategoryId: 'question-type',
            categoryName: props.contentCategoryLabel,
            children: helpMenu,
          })
        )
      }
    }

    menuData.value = [allCategoryItem, ...menuItems]
  } catch (error) {
    console.error('获取分类列表失败:', error)
    if (import.meta.client) {
      BaseMessage.error('获取分类列表失败')
    }
    if (!preserveExisting) {
      menuData.value = []
    }
  } finally {
    loading.value = false
  }
}

/**
 * @description 获取所有一级可展开的菜单项key
 * @param {Array} items - 菜单项数组
 * @returns {Array<string>} - 一级可展开key数组
 */
const expandAll = (items) => {
  if (!Array.isArray(items)) return []
  return items
    .filter((item) => Array.isArray(item.children) && item.children.length > 0)
    .map((item) => item.key)
}

/**
 * @description 获取第一条一级可展开的菜单项key（跳过无子节点的项如「全部分类」）
 * @param {Array} items - 菜单项数组
 * @returns {Array<string>} - 一级可展开key数组
 */
const expandFirst = (items) => {
  if (!Array.isArray(items) || items.length === 0) return []
  const firstWithChildren = items.find(
    (item) => item.children && Array.isArray(item.children) && item.children.length > 0
  )
  return firstWithChildren ? [firstWithChildren.key] : []
}

/**
 * @description 根据 defaultExpand 配置初始化展开的菜单项
 */
const initOpenKeys = () => {
  if (!menuData.value || menuData.value.length === 0) {
    return []
  }

  switch (props.defaultExpand) {
    case 'all':
      return expandAll(menuData.value)
    case 'first':
      return expandFirst(menuData.value)
    case 'none':
    default:
      return []
  }
}

const applyDefaultOpenKeys = () => {
  if (!menuData.value || menuData.value.length === 0) {
    return
  }

  let keys = []
  if (props.defaultExpand !== 'none') {
    keys = initOpenKeys()
  }

  // 确保选中项路径上的父级展开
  if (selectedPathKeys.value.length > 0) {
    keys = Array.from(new Set([...keys, ...selectedPathKeys.value]))
  }

  openKeys.value = keys
}

const { data: initialMenuData } = await useAsyncData(
  () =>
    `website-help-menu:${props.contentCenterType}:${props.contentCategoryRootName}:${props.categories.join(',')}`,
  async () => {
    if (import.meta.client) return []

    await fetchCategoryTree(createPublicContentSsrRequestConfig())
    return menuData.value
  },
  usePublicContentAsyncDataOptions({ default: () => [] })
)

if (import.meta.server || useNuxtApp().isHydrating) {
  menuData.value = Array.isArray(initialMenuData.value) ? initialMenuData.value : []
  hasManualOpen.value = false
  applyDefaultOpenKeys()
}

onMounted(async () => {
  await fetchCategoryTree({}, { preserveExisting: true })
  hasManualOpen.value = false
  applyDefaultOpenKeys()
})

watch(
  () => props.selectedKey,
  () => {
    if (selectedPathKeys.value.length > 0) {
      // 当选中项改变时，自动展开对应路径
      openKeys.value = Array.from(new Set([...openKeys.value, ...selectedPathKeys.value]))
    }
  }
)

watch(
  () => menuData.value,
  (value) => {
    if (!value || value.length === 0) return
    hasManualOpen.value = false
    applyDefaultOpenKeys()
  }
)

watch(
  () => props.defaultExpand,
  () => {
    if (hasManualOpen.value) return
    applyDefaultOpenKeys()
  }
)
</script>

<style lang="scss" scoped>
.service-menu {
  width: 100%;
  border-radius: 0;
  padding: 0;

  &__tree {
    border: none;
    background-color: transparent;
  }

  &__skeleton {
    width: 100%;
    padding: 4px 8px;
    box-sizing: border-box;
  }

  &__skeleton-row {
    display: flex;
    min-height: 38px;
    align-items: center;

    &--child {
      padding-left: 16px;
    }
  }

  :deep(.service-menu__skeleton .arco-skeleton-line) {
    width: 100%;
    margin: 0;
  }

  :deep(.service-menu__skeleton .arco-skeleton-line-row) {
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.25) 20%,
      $bg-white 50%,
      rgba(255, 255, 255, 0.25) 80%
    );
    background-size: 250% 100%;
    animation: service-menu-skeleton-shimmer 1.2s linear infinite;
  }

  :deep(.arco-menu-item) {
    padding-left: 8px !important;
    padding-right: 8px !important;
  }
}

@keyframes service-menu-skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0 50%;
  }
}

:deep(.arco-menu) {
  border: none;
  background-color: transparent;
}

:deep(.arco-menu-vertical .arco-menu-inner) {
  padding: 0;
}

:deep(.arco-menu-vertical .arco-menu-inline-header:not(.arco-menu-has-icon)) {
  padding-left: 8px;
  padding-right: 8px;
  padding-top: 8px;
  padding-bottom: 8px;
  background-color: transparent;
  display: flex;
  align-items: center;
  overflow: visible;
  white-space: normal;
  text-overflow: clip;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 22px;
  height: auto;
  min-height: 38px;
}

:deep(.arco-menu-vertical .arco-menu-inline-header.arco-menu-has-icon) {
  align-items: center;
  overflow: visible;
  white-space: normal;
  text-overflow: clip;
  padding-top: 8px;
  padding-bottom: 8px;
}

:deep(.arco-menu-vertical .arco-menu-inline-header .arco-menu-title) {
  flex: 1;
  min-width: 0;
  overflow: visible;
  white-space: normal;
  text-overflow: clip;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 22px;
}

:deep(.arco-menu-vertical .arco-menu-inline-header .arco-menu-item-inner) {
  overflow: visible;
  white-space: normal;
  text-overflow: clip;
  word-break: break-word;
  overflow-wrap: anywhere;
}

:deep(.arco-menu-light .arco-menu-inline-header.arco-menu-selected) {
  padding-left: 8px;
  padding-right: 8px;
  background-color: transparent;
  color: $text-title-color;
}

:deep(.arco-menu-light .arco-menu-inline-header.arco-menu-selected .arco-icon) {
  padding: 0;
  color: $text-caption-color;
}

:deep(.arco-menu-inline-header .arco-menu-icon-suffix),
:deep(.arco-menu-inline-header .arco-icon) {
  flex-shrink: 0;
  align-self: center;
  width: 16px;
  height: 16px;
  font-size: 16px;
  line-height: 16px;
  color: $text-caption-color;
}

:deep(.arco-menu-inline-header .arco-menu-icon-suffix svg),
:deep(.arco-menu-inline-header .arco-icon svg) {
  width: 16px;
  height: 16px;
}

:deep(.arco-menu-item) {
  height: auto;
  min-height: 38px;
  line-height: 22px;
  margin: 0;
  padding-top: 8px;
  padding-bottom: 8px;
  color: $text-content-color;
  font-size: 14px;
  background-color: transparent;
  white-space: normal;
  word-break: break-all;
}

:deep(.arco-menu-item:hover) {
  background-color: $bg-white;
  border-radius: 6px;
  color: $text-strong-color;
  font-size: 14px;
}

:deep(.arco-menu-item.arco-menu-selected) {
  background-color: $bg-white;
  color: $text-title-color;
  font-weight: 500;
  border-radius: 6px;
}

:deep(.arco-menu-item[data-menu-key='all'].arco-menu-selected),
:deep(.arco-menu-item.service-menu__item-all.arco-menu-selected) {
  background-color: transparent;
  color: $text-title-color;
  font-weight: 400;
}

:deep(.arco-menu-item[data-menu-key='all']),
:deep(.arco-menu-item.service-menu__item-all) {
  color: $text-title-color;
}

@include respond-to-max('lg') {
  :deep(.arco-menu-item) {
    min-height: 38px;
    line-height: 22px;
    padding-top: 8px;
    padding-bottom: 8px;
    font-size: 14px;
  }
}

/* 选中路径同步文字颜色，字重由层级本身决定 */
:deep(.arco-sub-menu.service-menu__in-selected-path > .arco-sub-menu-title),
:deep(.arco-menu-inline.service-menu__in-selected-path > .arco-menu-inline-header),
:deep(.arco-menu-item.service-menu__in-selected-path) {
  color: $text-title-color;
}

:deep(.arco-menu-vertical .arco-menu-inline-header) {
  margin: 0;
  color: $text-title-color;
  font-size: 14px;
  font-weight: 500;
  min-height: 38px;
  line-height: 22px;
}

:deep(.arco-menu-vertical .arco-menu-inline-header:hover) {
  background-color: $bg-white;
  border-radius: 6px;
  color: $text-strong-color;
}

@include respond-to-max('lg') {
  :deep(.arco-menu-vertical .arco-menu-inline-header) {
    min-height: 38px;
    padding-top: 8px;
    padding-bottom: 8px;
  }
}

:deep(.arco-menu-vertical .arco-menu-inline-content .arco-menu-inline-header) {
  font-weight: 400;
}

:deep(.arco-menu-vertical .arco-menu-item:not(.arco-menu-has-icon)) {
  display: flex;
  align-items: flex-start;
  overflow: visible;
  line-height: 22px;
}

:deep(.arco-menu-vertical .arco-menu-item .arco-menu-item-inner) {
  flex: 1;
  min-width: 0;
  overflow: visible;
  white-space: normal;
  word-break: break-all;
  line-height: 22px;
}
</style>
